import { GameTab, TabMountContext } from '../../engine/tabs';
import { icon } from '../../engine/icons';
import { authStore } from '../../core/state/auth-store';
import { showToast } from '../../engine/toast';
import { applyReduceMotion, isReduceMotionEnabled, setReduceMotionEnabled } from '../../engine/reduce-motion';
import { languageSwitcherHtml } from '../../engine/i18n/language-switcher';
import { getLocale, setLocale, t, type Locale } from '../../engine/i18n/i18n';
import { audioStore, setMuted, setVolume, setSfxVolume } from '../../engine/audio';
import { SIDE_MENU_OPEN, mountSideMenu, setSideMenuChip, sideMenuBarHtml } from '../../engine/side-menu';
import {
  mountBillingAddressForm,
  mountPaymentHistory,
  mountWithdrawForm,
  getWithdrawalLimits,
  getPaymentMethods,
  toPaymentError,
} from '../../core/payments';
import type { PayoutMethod, WithdrawalLimits } from '../../core/payments';
import { fetchStoreBalance } from '../shop/shop-api';
import { renderAdminScreen } from '../admin/admin-screen';
import { kycPaneHtml, mountKycPane } from './kyc-pane';
import {
  fetchAccount,
  updateAccount,
  changePassword,
  deleteAccount,
  type AccountView,
} from './settings-api';

/**
 * Account, billing, withdrawals and preferences, behind a pane rail.
 *
 * This screen was 162 lines that showed a read-only name and e-mail and
 * admitted in plain text that "change password and account deletion aren't
 * available yet". The panes are the same four splits Steel of War uses —
 * Account, Billing, Withdrawals, Preferences — re-expressed in this repo's
 * vanilla-TS `innerHTML` idiom rather than ported: SoW is Vue SFCs, so there
 * was never a 1:1 copy available to make.
 *
 * Billing and Withdrawals mount core's own payment widgets instead of
 * reimplementing them. Those arrived with the template merge carrying the
 * fiddly parts — IBAN and card validation, country lists, the error-code
 * vocabulary — so a game-side copy would only have been a worse one.
 */

type PaneId = 'account' | 'billing' | 'withdrawals' | 'kyc' | 'preferences';

function panes(): { id: PaneId; label: string; blurb: string }[] {
  return [
    { id: 'account', label: t('settings.pane.account'), blurb: t('settings.pane.accountBlurb') },
    { id: 'billing', label: t('settings.pane.billing'), blurb: t('settings.pane.billingBlurb') },
    { id: 'withdrawals', label: t('settings.pane.withdrawals'), blurb: t('settings.pane.withdrawalsBlurb') },
    { id: 'kyc', label: t('settings.pane.kyc'), blurb: t('settings.pane.kycBlurb') },
    { id: 'preferences', label: t('settings.pane.preferences'), blurb: t('settings.pane.preferencesBlurb') },
  ];
}

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

function errorMessage(err: unknown, fallback: string): string {
  const paymentError = toPaymentError(err);
  if (paymentError?.message) return paymentError.message;
  return err instanceof Error ? err.message : fallback;
}

export function renderSettingsScreen(root: HTMLElement, ctx?: TabMountContext): () => void {
  let disposed = false;
  let activePane: PaneId = 'account';
  let account: AccountView | null = null;
  let payoutMethods: PayoutMethod[] = [];
  let withdrawalLimits: WithdrawalLimits | null = null;
  let premiumBalance = 0;
  let busy = false;

  /**
   * When the admin screen is open it takes over the whole mount point. Kept as
   * a teardown rather than a flag so the settings screen's own teardown
   * unmounts it too.
   */
  let adminTeardown: (() => void) | null = null;

  /** Teardowns for whichever core widgets the current pane mounted. */
  let widgetTeardowns: Array<() => void> = [];

  function disposeWidgets(): void {
    for (const teardown of widgetTeardowns) {
      try {
        teardown();
      } catch {
        // One widget failing to tear down must not strand the others.
      }
    }
    widgetTeardowns = [];
  }

  let railEl!: HTMLElement;
  let paneEl!: HTMLElement;
  let logoutBtn!: HTMLButtonElement;

  /**
   * Builds the settings shell and re-captures its elements.
   *
   * Callable more than once because the admin screen takes over the same mount
   * point: returning from it has to rebuild this markup, and the previously
   * captured element references are detached by then.
   */
  function buildShell(): void {
    root.innerHTML = `
      <div class="w-full h-full flex flex-col">
        <div class="flex items-center justify-between gap-2 px-3 py-2 border-b border-border shrink-0">
          <h1 class="font-heading text-gold text-base">${t('nav.settings')}</h1>
          <button type="button" id="settings-logout-btn"
            class="min-h-11 px-3 text-xs text-crimson border border-crimson/40 rounded hover:bg-crimson/10 transition-colors duration-[120ms] touch-manipulation">
            ${t('settings.logOut')}
          </button>
        </div>
        <div class="flex-1 min-h-0 overflow-y-auto">
          <div class="p-4 sm:p-6 max-w-4xl mx-auto flex flex-col sm:flex-row gap-4 sm:gap-6 pb-10">
            ${SIDE_MENU_OPEN}
              ${sideMenuBarHtml(panes().find((p) => p.id === activePane)?.label ?? '', t('settings.sections'), 'settings-rail')}
              <nav aria-label="${t('settings.sections')}" id="settings-rail"
                class="side-menu-panel holo-rail shrink-0 sm:w-48 flex sm:flex-col gap-1 sm:overflow-visible sm:sticky sm:top-0 sm:self-start"></nav>
            </div>
            <div class="flex-1 min-w-0" id="settings-pane"></div>
          </div>
        </div>
      </div>
    `;
    railEl = root.querySelector('#settings-rail') as HTMLElement;
    paneEl = root.querySelector('#settings-pane') as HTMLElement;
    const nextLogout = root.querySelector('#settings-logout-btn') as HTMLButtonElement;
    nextLogout.addEventListener('click', handleLogout);
    logoutBtn = nextLogout;
  }

  function paintRail(): void {
    setSideMenuChip(root, panes().find((p) => p.id === activePane)?.label ?? '');
    railEl.innerHTML = panes().map(
      (p) => `
        <button type="button" data-action="pick-pane" data-pane="${p.id}"
          aria-current="${p.id === activePane ? 'page' : 'false'}"
          class="w-full text-left min-h-11 px-3 py-2 border-l-[3px] transition-colors duration-[120ms] touch-manipulation ${
            p.id === activePane
              ? 'border-l-gold bg-surface-2 text-text'
              : 'border-l-transparent text-text-dim hover:text-text'
          }">
          <span class="block text-[11px] uppercase tracking-[0.14em]">${p.label}</span>
          <span class="block text-[10px] text-text-dim leading-snug">${p.blurb}</span>
        </button>`,
    ).join('');
  }

  function accountPaneHtml(): string {
    const a = account;
    const verified = a?.emailVerified
      ? `<span class="text-[10px] text-success">${t('settings.verified')}</span>`
      : `<span class="text-[10px] text-crimson">${t('settings.unverified')}</span>`;
    return `
      <section class="flex flex-col gap-4">
        <div>
          <h2 class="font-heading text-xs text-gold mb-1 tracking-wide uppercase">${t('settings.identity')}</h2>
          <div class="glass-panel px-3 py-3 flex flex-col gap-3">
            <div>
              <div class="text-[10px] text-text-dim">${t('settings.email')} ${verified}</div>
              <div class="text-sm text-text truncate" data-account-email>${escapeHtml(a?.email ?? '—')}</div>
            </div>
            <form data-form="rename" class="flex flex-col gap-1">
              <label class="text-[10px] text-text-dim" for="settings-username">${t('settings.commanderName')}</label>
              <div class="flex gap-2">
                <input id="settings-username" name="username" type="text" value="${escapeHtml(a?.username ?? '')}"
                  minlength="3" maxlength="32" required
                  class="flex-1 min-w-0 min-h-11 px-2 bg-surface-2 border border-border rounded text-text text-sm focus:border-gold outline-none" />
                <button type="submit" ${busy ? 'disabled' : ''}
                  class="font-button min-h-11 px-4 bg-gold text-bg text-sm rounded hover:bg-gold-bright disabled:bg-border disabled:text-text-dim touch-manipulation">${t('common.save')}</button>
              </div>
            </form>
            <div class="grid grid-cols-2 gap-3 text-[10px] text-text-dim">
              <div>${t('common.level')} <span class="text-text tabular-nums">${a?.level ?? '—'}</span></div>
              <div>${t('settings.enlisted')} <span class="text-text">${a ? new Date(a.createdAt).toISOString().slice(0, 10) : '—'}</span></div>
            </div>
          </div>
        </div>

        <div>
          <h2 class="font-heading text-xs text-gold mb-1 tracking-wide uppercase">${t('settings.password')}</h2>
          <form data-form="password" class="glass-panel px-3 py-3 flex flex-col gap-2">
            <input name="currentPassword" type="password" placeholder="${t('settings.currentPassword')}" autocomplete="current-password" required
              class="min-h-11 px-2 bg-surface-2 border border-border rounded text-text text-sm focus:border-gold outline-none" />
            <input name="newPassword" type="password" placeholder="${t('settings.newPassword')}" autocomplete="new-password" minlength="8" required
              class="min-h-11 px-2 bg-surface-2 border border-border rounded text-text text-sm focus:border-gold outline-none" />
            <input name="repeatPassword" type="password" placeholder="${t('settings.repeatPassword')}" autocomplete="new-password" minlength="8" required
              class="min-h-11 px-2 bg-surface-2 border border-border rounded text-text text-sm focus:border-gold outline-none" />
            <p class="text-[10px] text-text-dim leading-relaxed">${t('settings.passwordHint')}</p>
            <button type="submit" ${busy ? 'disabled' : ''}
              class="font-button min-h-11 px-4 bg-gold text-bg text-sm rounded hover:bg-gold-bright disabled:bg-border disabled:text-text-dim touch-manipulation">${t('settings.changePassword')}</button>
          </form>
        </div>

        <div>
          <h2 class="font-heading text-xs text-crimson mb-1 tracking-wide uppercase">${t('settings.dangerZone')}</h2>
          <form data-form="delete" class="glass-panel px-3 py-3 flex flex-col gap-2 border border-crimson/30">
            <p class="text-[10px] text-text-dim leading-relaxed">
              ${t('settings.closeHint')}
            </p>
            <input name="password" type="password" placeholder="${t('settings.confirmWithPassword')}" autocomplete="current-password" required
              class="min-h-11 px-2 bg-surface-2 border border-border rounded text-text text-sm focus:border-crimson outline-none" />
            <button type="submit" ${busy ? 'disabled' : ''}
              class="font-button min-h-11 px-4 bg-crimson/10 border border-crimson/40 text-crimson text-sm rounded hover:bg-crimson/20 disabled:opacity-50 touch-manipulation">${t('settings.closeAccount')}</button>
          </form>
        </div>
      </section>
    `;
  }

  /**
   * Shown only to an account whose session says ADMIN. This is a courtesy, not
   * access control: every /api/admin/* route is re-checked server-side by
   * AdminAccessGuard, so hiding the button grants nothing and showing it gives
   * nothing away.
   */
  function isAdmin(): boolean {
    return authStore.get().user?.role === 'ADMIN';
  }

  function adminEntryHtml(): string {
    if (!isAdmin()) return '';
    return `
      <div>
        <h2 class="font-heading text-xs text-gold mb-3 tracking-wide uppercase">${t('settings.administration')}</h2>
        <div class="glass-panel px-3 py-3 flex items-center justify-between gap-3">
          <div class="min-w-0">
            <div class="text-sm text-text">${t('settings.adminPanel')}</div>
            <div class="text-[10px] text-text-dim leading-relaxed">${t('settings.adminPanelHint')}</div>
          </div>
          <button type="button" data-action="open-admin"
            class="font-button shrink-0 min-h-11 px-4 bg-gold text-bg text-sm rounded hover:bg-gold-bright touch-manipulation flex items-center gap-1.5">${t('common.open')} <span class="w-3.5 h-3.5">${icon('externalLink')}</span></button>
        </div>
      </div>`;
  }

  function preferencesPaneHtml(): string {
    const on = isReduceMotionEnabled();
    const audio = audioStore.get();
    return `
      <section class="flex flex-col gap-12">
       <div>
        <h2 class="font-heading text-xs text-gold mb-3 tracking-wide uppercase">${t('settings.audio')}</h2>
        <div class="glass-panel px-4 py-4 flex flex-col gap-5">
          <button type="button" data-action="toggle-music" role="switch" aria-checked="${!audio.muted}"
            class="w-full min-h-11 flex items-center justify-between gap-3 py-1 text-left touch-manipulation">
            <span class="min-w-0">
              <span class="block text-sm text-text">${t('settings.music')}</span>
              <span class="block text-[10px] text-text-dim leading-relaxed">${t('settings.musicHint')}</span>
            </span>
            <span aria-hidden="true"
              class="shrink-0 relative w-11 h-6 rounded-full border transition-colors duration-[120ms] ${!audio.muted ? 'bg-gold border-gold-bright' : 'bg-surface-2 border-border'}">
              <span class="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-text-dim transition-transform duration-[120ms] ${!audio.muted ? 'translate-x-5' : ''}"></span>
            </span>
          </button>
          <label class="flex items-center gap-3">
            <span class="text-[10px] text-text-dim w-14 shrink-0">${t('settings.volume')}</span>
            <input type="range" data-action="set-volume" min="0" max="100" step="5" value="${Math.round(audio.volume * 100)}"
              aria-label="${t('settings.musicVolume')}" class="flex-1 accent-gold touch-manipulation" />
          </label>
          <label class="flex items-center gap-3">
            <span class="text-[10px] text-text-dim w-14 shrink-0">${t('settings.sfx')}</span>
            <input type="range" data-action="set-sfx-volume" min="0" max="100" step="5" value="${Math.round(audio.sfxVolume * 100)}"
              aria-label="${t('settings.sfxVolume')}" class="flex-1 accent-gold touch-manipulation" />
          </label>
        </div>
       </div>

       <div>
        <h2 class="font-heading text-xs text-gold mb-3 tracking-wide uppercase">${t('settings.preferences')}</h2>
        <div class="glass-panel px-4 py-4 flex flex-col gap-5">
          <button type="button" data-action="toggle-reduce-motion" role="switch" aria-checked="${on}"
            class="w-full min-h-11 flex items-center justify-between gap-3 py-1 text-left touch-manipulation">
            <span class="min-w-0">
              <span class="block text-sm text-text">${t('settings.reduceMotion')}</span>
              <span class="block text-[10px] text-text-dim leading-relaxed">${t('settings.reduceMotionHint')}</span>
            </span>
            <span aria-hidden="true" data-toggle-track
              class="shrink-0 relative w-11 h-6 rounded-full border transition-colors duration-[120ms] ${on ? 'bg-gold border-gold-bright' : 'bg-surface-2 border-border'}">
              <span data-toggle-thumb
                class="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-text-dim transition-transform duration-[120ms] ${on ? 'translate-x-5' : ''}"></span>
            </span>
          </button>
          <div class="border-t border-border/60 pt-5">
            ${languageSwitcherHtml('full')}
          </div>
        </div>
       </div>

        ${adminEntryHtml()}
      </section>
    `;
  }

  function billingPaneHtml(): string {
    return `
      <section class="flex flex-col gap-4">
        <div>
          <h2 class="font-heading text-xs text-gold mb-1 tracking-wide uppercase">${t('settings.billingAddress')}</h2>
          <div class="glass-panel px-3 py-3" data-widget="billing-address"></div>
        </div>
        <div>
          <h2 class="font-heading text-xs text-gold mb-1 tracking-wide uppercase">${t('settings.invoicesReceipts')}</h2>
          <div class="glass-panel px-3 py-3" data-widget="payment-history"></div>
        </div>
      </section>
    `;
  }

  function withdrawalsPaneHtml(): string {
    if (!withdrawalLimits) {
      return `
        <section>
          <h2 class="font-heading text-xs text-gold mb-1 tracking-wide uppercase">${t('settings.withdrawals')}</h2>
          <div class="glass-panel px-3 py-3 text-xs text-text-dim">${t('settings.checkingAvailability')}</div>
        </section>`;
    }
    if (!withdrawalLimits.enabled || payoutMethods.length === 0) {
      // Honest about why, rather than rendering a form that cannot submit:
      // this instance has no payout provider configured.
      return `
        <section>
          <h2 class="font-heading text-xs text-gold mb-1 tracking-wide uppercase">${t('settings.withdrawals')}</h2>
          <div class="glass-panel px-3 py-3 flex flex-col gap-1" data-withdrawals-unavailable>
            <p class="text-sm text-text">${t('settings.withdrawalsUnavailable')}</p>
            <p class="text-[10px] text-text-dim leading-relaxed">${t('settings.withdrawalsUnavailableHint')}</p>
          </div>
        </section>`;
    }
    return `
      <section>
        <h2 class="font-heading text-xs text-gold mb-1 tracking-wide uppercase">${t('settings.withdrawals')}</h2>
        <div class="glass-panel px-3 py-3" data-widget="withdraw"></div>
      </section>
    `;
  }

  function mountBillingWidgets(): void {
    const addressEl = paneEl.querySelector('[data-widget="billing-address"]') as HTMLElement | null;
    if (addressEl) {
      const handle = mountBillingAddressForm(addressEl, {
        onSaved: () => showToast(t('settings.billingSaved'), 'success'),
      });
      widgetTeardowns.push(() => handle.destroy());
    }
    const historyEl = paneEl.querySelector('[data-widget="payment-history"]') as HTMLElement | null;
    if (historyEl) {
      const handle = mountPaymentHistory(historyEl, { withdrawals: true });
      widgetTeardowns.push(() => handle.destroy());
    }
  }

  function mountWithdrawWidget(): void {
    const el = paneEl.querySelector('[data-widget="withdraw"]') as HTMLElement | null;
    if (!el || !withdrawalLimits) return;
    const handle = mountWithdrawForm(el, {
      methods: payoutMethods,
      limits: withdrawalLimits,
      gameCurrency: { name: 'Delta-V', perEur: 100, balance: premiumBalance },
      onSubmitted: () => showToast(t('settings.withdrawalRequested'), 'success'),
    });
    widgetTeardowns.push(() => handle.destroy());
  }

  function paintPane(): void {
    disposeWidgets();
    if (activePane === 'account') paneEl.innerHTML = accountPaneHtml();
    else if (activePane === 'preferences') paneEl.innerHTML = preferencesPaneHtml();
    else if (activePane === 'kyc') {
      paneEl.innerHTML = kycPaneHtml(account);
      // Re-fetch AND repaint: a successful submission moves the status to
      // PENDING, which is what decides whether the form renders at all.
      // `loadAccount` only refreshes the record — it does not paint, so
      // without the explicit render the pane kept showing "Not submitted"
      // after a submission that had in fact succeeded.
      widgetTeardowns.push(
        mountKycPane(paneEl, () => {
          void loadAccount().then(() => {
            if (!disposed) render();
          });
        }),
      );
    } else if (activePane === 'billing') {
      paneEl.innerHTML = billingPaneHtml();
      mountBillingWidgets();
    } else {
      paneEl.innerHTML = withdrawalsPaneHtml();
      mountWithdrawWidget();
    }
  }

  function render(): void {
    paintRail();
    paintPane();
  }

  async function loadAccount(): Promise<void> {
    try {
      account = await fetchAccount();
    } catch {
      // Fall back to what the auth store already holds rather than blanking
      // the pane — same philosophy as colony-screen's background resync.
      const user = authStore.get().user;
      if (user) {
        account = {
          id: user.id,
          email: user.email,
          username: user.username ?? null,
          emailVerified: true,
          kycStatus: 'NONE',
          level: 1,
          xp: 0,
          createdAt: new Date().toISOString(),
        };
      }
    }
  }

  /**
   * Withdrawal availability is read up front so the pane can say "not
   * available here" instead of mounting a form against a provider that does
   * not exist. A failed read degrades to the same message.
   */
  async function loadWithdrawalContext(): Promise<void> {
    const [limits, methods, bal] = await Promise.allSettled([getWithdrawalLimits(), getPaymentMethods(), fetchStoreBalance()]);
    if (disposed) return;
    withdrawalLimits = limits.status === 'fulfilled' ? limits.value : ({ enabled: false } as WithdrawalLimits);
    payoutMethods = methods.status === 'fulfilled' ? methods.value.withdrawals.methods : [];
    premiumBalance = bal.status === 'fulfilled' ? bal.value.premium : 0;
  }

  async function handleRename(form: HTMLFormElement): Promise<void> {
    if (busy) return;
    const username = String(new FormData(form).get('username') ?? '').trim();
    if (!username) return;
    busy = true;
    try {
      account = await updateAccount(username);
      if (disposed) return;
      showToast(t('settings.nameUpdated'), 'success');
      render();
    } catch (err) {
      if (disposed) return;
      showToast(errorMessage(err, t('settings.nameUpdateFailed')), 'error');
    } finally {
      busy = false;
    }
  }

  async function handleChangePassword(form: HTMLFormElement): Promise<void> {
    if (busy) return;
    const data = new FormData(form);
    const current = String(data.get('currentPassword') ?? '');
    const next = String(data.get('newPassword') ?? '');
    const repeat = String(data.get('repeatPassword') ?? '');

    // Checked here, not server-side: the repeat field exists only to catch a
    // typo, and the server has no business knowing it was offered.
    if (next !== repeat) {
      showToast(t('settings.passwordMismatch'), 'error');
      return;
    }

    busy = true;
    try {
      await changePassword(current, next);
      if (disposed) return;
      form.reset();
      showToast(t('settings.passwordChanged'), 'success');
    } catch (err) {
      if (disposed) return;
      showToast(errorMessage(err, t('settings.passwordChangeFailed')), 'error');
    } finally {
      busy = false;
    }
  }

  async function handleDeleteAccount(form: HTMLFormElement): Promise<void> {
    if (busy) return;
    const password = String(new FormData(form).get('password') ?? '');
    if (!password) return;
    if (!window.confirm(t('settings.closeConfirm'))) return;

    busy = true;
    try {
      const { mode } = await deleteAccount(password);
      if (disposed) return;
      showToast(
        mode === 'anonymised' ? t('settings.accountClosedRetained') : t('settings.accountClosed'),
        'success',
      );
      ctx?.onLogout?.();
    } catch (err) {
      if (disposed) return;
      showToast(errorMessage(err, t('settings.closeFailed')), 'error');
    } finally {
      busy = false;
    }
  }

  function handleToggleReduceMotion(): void {
    const next = !isReduceMotionEnabled();
    setReduceMotionEnabled(next);
    applyReduceMotion(next);
    paintPane();
  }

  /**
   * Switching language reloads the page rather than re-rendering in place.
   *
   * A re-render would have to reach the shell header, the tab bar, every
   * mounted screen AND the six core auth screens, which cannot subscribe to
   * anything (`frontend/src/core/**` is template-owned). A reload gets all of
   * it, plus `<html lang>`, for free. It is a once-a-session action, so the
   * cost is a page load the player explicitly asked for — not a re-render
   * framework this app otherwise has no need of.
   */
  function handleSetLocale(next: Locale | undefined): void {
    if (!next || next === getLocale()) return;
    setLocale(next);
    window.location.reload();
  }

  function onClick(e: MouseEvent): void {
    const el = (e.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
    if (!el) return;
    if (el.dataset.action === 'pick-pane') {
      activePane = el.dataset.pane as PaneId;
      render();
    } else if (el.dataset.action === 'set-locale') {
      handleSetLocale(el.dataset.locale as Locale | undefined);
    } else if (el.dataset.action === 'toggle-reduce-motion') {
      handleToggleReduceMotion();
    } else if (el.dataset.action === 'toggle-music') {
      setMuted(!audioStore.get().muted);
      paintPane();
    } else if (el.dataset.action === 'open-admin') {
      openAdmin();
    }
  }

  /**
   * Hands the whole mount point to the admin screen and takes it back on exit.
   * A nested mount rather than a route because this app has no router — the
   * shell swaps tabs by tearing down and re-mounting into the same div.
   */
  function openAdmin(): void {
    if (adminTeardown) return;
    disposeWidgets();
    const previousPane = activePane;
    adminTeardown = renderAdminScreen(root, () => {
      adminTeardown?.();
      adminTeardown = null;
      activePane = previousPane;
      buildShell();
      render();
    });
  }

  function onSubmit(e: Event): void {
    const form = e.target as HTMLFormElement;
    const kind = form.dataset?.form;
    if (!kind) return;
    e.preventDefault();
    if (kind === 'rename') void handleRename(form);
    else if (kind === 'password') void handleChangePassword(form);
    else if (kind === 'delete') void handleDeleteAccount(form);
  }

  function handleLogout(): void {
    ctx?.onLogout?.();
  }

  /**
   * Volume is patched live rather than through paintPane(): a repaint on every
   * drag frame replaces the <input> the pointer is holding and drops the drag.
   */
  function onInput(e: Event): void {
    const el = e.target as HTMLElement;
    if (el.matches('[data-action="set-volume"]')) {
      setVolume(Number((el as HTMLInputElement).value) / 100);
    } else if (el.matches('[data-action="set-sfx-volume"]')) {
      setSfxVolume(Number((el as HTMLInputElement).value) / 100);
    }
  }

  const unmountSideMenu = mountSideMenu(root);
  root.addEventListener('click', onClick);
  root.addEventListener('input', onInput);
  root.addEventListener('submit', onSubmit);

  buildShell();
  applyReduceMotion(isReduceMotionEnabled());
  render();
  void (async () => {
    await Promise.all([loadAccount(), loadWithdrawalContext()]);
    if (disposed) return;
    render();
  })();

  return () => {
    disposed = true;
    adminTeardown?.();
    adminTeardown = null;
    disposeWidgets();
    unmountSideMenu();
    root.removeEventListener('click', onClick);
    root.removeEventListener('input', onInput);
    root.removeEventListener('submit', onSubmit);
    logoutBtn?.removeEventListener('click', handleLogout);
  };
}

export const settingsTab: GameTab = {
  id: 'settings',
  label: t('nav.settings'),
  icon: 'settings',
  mount: renderSettingsScreen,
};
